import React, { useState } from 'react';
import {
  Gavel,
  Layers,
  Zap,
  CheckCircle2,
  Clock,
  ArrowRight,
} from 'lucide-react';

const STRATEGIES = [
  {
    id: 'wfa',
    name: 'Wait for All',
    short: 'WFA',
    icon: Gavel,
    headline: 'Run a sealed auction. Auto-pick the best quote.',
    description:
      'Send the load to your carrier pool, collect every quote within a time window, and automatically award the load to the lowest qualifying bid.',
    bestFor: 'Loads with flexible timing where price is the priority.',
    bullets: [
      'Configure window length, carrier list, and minimum bidders',
      'Auto-award to lowest qualifying carrier when timer ends',
      'Full audit log of every quote and decision',
    ],
    timeline: [
      { label: 'Tender sent to 12 carriers', state: 'done', time: '10:00' },
      { label: '4 quotes received', state: 'done', time: '10:08' },
      { label: 'Window closes', state: 'done', time: '10:30' },
      { label: 'Lowest qualified carrier awarded', state: 'win', time: '10:30' },
    ],
  },
  {
    id: 'waterfall',
    name: 'Waterfall',
    short: 'WTF',
    icon: Layers,
    headline: 'Offer your target rate. First "yes" wins.',
    description:
      'Send predefined rates down a ranked list of carriers. The first carrier to accept your target rate locks in the load — no negotiation needed.',
    bestFor: 'Lanes where you already know a fair rate and want speed.',
    bullets: [
      'Define rate, lane, and ranked carrier sequence',
      'Auto-roll to the next carrier after timeout',
      'Lock in coverage at your target — not theirs',
    ],
    timeline: [
      { label: 'Offer sent to Carrier A @ $2,300', state: 'done', time: '10:00' },
      { label: 'No response · rolled to Carrier B', state: 'done', time: '10:05' },
      { label: 'Carrier B accepted', state: 'win', time: '10:06' },
      { label: 'Load booked', state: 'pending', time: '10:06' },
    ],
  },
  {
    id: 'critical',
    name: 'Critical',
    short: 'CRT',
    icon: Zap,
    headline: 'Time is short. Cover the load fast.',
    description:
      'Send a quote request to a curated list of trusted carriers and award the first carrier who can take the load — perfect for hot freight near pickup.',
    bestFor: 'Loads close to pickup where coverage matters more than savings.',
    bullets: [
      'Trigger by pickup proximity or manual override',
      'Hand-picked list of fastest-responding carriers',
      'First qualifying yes wins — automatically',
    ],
    timeline: [
      { label: 'Pickup in 3h · Critical triggered', state: 'done', time: '13:02' },
      { label: 'Sent to top 6 fast carriers', state: 'done', time: '13:02' },
      { label: 'Carrier C says YES', state: 'win', time: '13:04' },
      { label: 'Truck dispatched', state: 'pending', time: '13:05' },
    ],
  },
];

export default function Strategies() {
  const [activeId, setActiveId] = useState('wfa');
  const active = STRATEGIES.find((s) => s.id === activeId);

  return (
    <section id="strategies" className="bg-ink-50 border-y border-ink-200">
      <div className="container-page py-20 lg:py-28">
        <div className="max-w-3xl">
          <p className="eyebrow" id="how-it-works">How it works</p>
          <h2 className="mt-3 h-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-balance">
            Three strategies. Every load tendered the right way.
          </h2>
          <p className="mt-5 text-lg text-ink-600 text-balance">
            Vibe Engine doesn’t force you into one workflow. Pick the strategy
            that fits each lane, each customer, and each level of urgency — or
            mix all three.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {STRATEGIES.map((s) => {
            const isActive = s.id === activeId;
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveId(s.id)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                  isActive
                    ? 'bg-ink-900 text-white border-ink-900'
                    : 'bg-white text-ink-700 border-ink-200 hover:border-ink-300 hover:text-ink-900'
                }`}
                aria-pressed={isActive}
              >
                <Icon className="h-4 w-4" />
                {s.name}
                <span
                  className={`hidden sm:inline text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded ${
                    isActive ? 'bg-white/15 text-white' : 'bg-ink-100 text-ink-500'
                  }`}
                >
                  {s.short}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-6 card-surface p-7 sm:p-8">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-ink-900 text-white">
                <active.icon className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
                  {active.short} · Strategy
                </div>
                <div className="text-base font-semibold text-ink-900">
                  {active.name}
                </div>
              </div>
            </div>

            <h3 className="mt-6 h-display text-2xl font-bold leading-tight text-balance">
              {active.headline}
            </h3>
            <p className="mt-3 text-ink-600">{active.description}</p>

            <div className="mt-6 rounded-xl border border-ink-200 bg-ink-50 px-4 py-3">
              <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
                Best for
              </div>
              <div className="text-sm text-ink-800 mt-1">{active.bestFor}</div>
            </div>

            <ul className="mt-6 space-y-3">
              {active.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-ink-700">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-ink-900 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6 card-surface p-7 sm:p-8">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-ink-900">
                Live tender · #VBE-{active.id.toUpperCase()}-001
              </div>
              <span className="pill">
                <Clock className="h-3 w-3" /> {active.name}
              </span>
            </div>

            <ol className="mt-6 relative">
              <span
                aria-hidden="true"
                className="absolute left-[15px] top-2 bottom-2 w-px bg-ink-200"
              />
              {active.timeline.map((step, i) => (
                <li key={i} className="relative pl-10 pb-5 last:pb-0">
                  <span
                    className={`absolute left-0 top-0 inline-flex h-8 w-8 items-center justify-center rounded-full border ${
                      step.state === 'win'
                        ? 'bg-ink-900 border-ink-900 text-white'
                        : step.state === 'done'
                        ? 'bg-white border-ink-300 text-ink-700'
                        : 'bg-ink-50 border-ink-200 text-ink-400'
                    }`}
                  >
                    {step.state === 'win' ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <span className="text-[11px] font-semibold tabular-nums">
                        {i + 1}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center justify-between gap-3">
                    <div
                      className={`text-sm ${
                        step.state === 'win'
                          ? 'font-semibold text-ink-900'
                          : 'text-ink-700'
                      }`}
                    >
                      {step.label}
                    </div>
                    <div className="text-xs tabular-nums text-ink-500">
                      {step.time}
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-6 flex items-center justify-between rounded-xl border border-ink-200 bg-white px-4 py-3">
              <div className="text-xs text-ink-600">Outcome</div>
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-ink-900">
                Load covered automatically
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
